import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  FlaskConical,
  Play,
  Filter,
  GitBranch,
  Library,
  FileText,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Copy,
  ChevronDown,
  ChevronUp,
  Scale,
  AlertTriangle,
  Shield,
  Gavel,
  FileWarning,
  Users,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// SAMPLE TEST CASES
// ============================================

const SAMPLE_TEST_CASES = [
  // Standard Credit Dispute Cases
  {
    id: 'test_001',
    name: 'Heavy Structural Damage',
    category: 'standard',
    input: {
      late_30_count: 3,
      late_60_count: 2,
      late_90_count: 1,
      has_collection: false,
      has_repossession: false,
      has_charge_off: true,
      has_bankruptcy: false,
    },
    expected: {
      classification: 'structural',
      method: 'method_structural_r1',
    },
  },
  {
    id: 'test_002',
    name: 'Collection Account Present',
    category: 'standard',
    input: {
      late_30_count: 0,
      late_60_count: 0,
      late_90_count: 0,
      has_collection: true,
      has_repossession: false,
      has_charge_off: false,
      has_bankruptcy: false,
    },
    expected: {
      classification: 'recovery',
      method: 'method_recovery_r1',
    },
  },
  {
    id: 'test_003',
    name: 'Minor Late Payment',
    category: 'standard',
    input: {
      late_30_count: 1,
      late_60_count: 0,
      late_90_count: 0,
      has_collection: false,
      has_repossession: false,
      has_charge_off: false,
      has_bankruptcy: false,
    },
    expected: {
      classification: 'isolated',
      method: 'method_isolated_r1',
    },
  },
  
  // ============================================
  // BANKRUPTCY TEST CASES
  // ============================================
  
  // Chapter 7 Public Record Case
  {
    id: 'bk_ch7_public_record',
    name: 'Chapter 7 Public Record - Wrong Filing Date',
    category: 'bankruptcy',
    subcategory: 'public_record',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: false,
      years_since_filing: 5,
      incorrect_filing_date: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_public_record_dispute',
      method: 'bankruptcy_public_record_metadata_dispute_workflow',
    },
  },
  
  // Chapter 13 Obsolete Case
  {
    id: 'bk_ch13_obsolete',
    name: 'Chapter 13 Obsolete Reporting (8+ Years)',
    category: 'bankruptcy',
    subcategory: 'obsolete',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_13',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: false,
      years_since_filing: 8,
    },
    expected: {
      dispute_lane: 'bankruptcy_obsolete_reporting',
      method: 'bankruptcy_obsolete_reporting_dispute_workflow',
    },
  },
  
  // Attached Tradeline Factual Case
  {
    id: 'bk_tradeline_factual',
    name: 'Attached Tradeline - Non-Zero Balance After Discharge',
    category: 'bankruptcy',
    subcategory: 'attached_tradeline_factual',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: true,
      years_since_filing: 3,
      tradeline_nonzero_balance: true,
      tradeline_status_missing: true,
    },
    expected: {
      dispute_lane: 'post_bankruptcy_tradeline_factual',
      method: 'post_bankruptcy_tradeline_factual_dispute_workflow',
    },
  },
  
  // Attached Tradeline Metro 2 Case
  {
    id: 'bk_tradeline_metro2',
    name: 'Attached Tradeline - Metro 2 Field Conflict',
    category: 'bankruptcy',
    subcategory: 'attached_tradeline_metro2',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: true,
      years_since_filing: 4,
      metro2_balance_should_be_zero: true,
      metro2_chargeoff_instead_of_bankruptcy: true,
    },
    expected: {
      dispute_lane: 'post_bankruptcy_tradeline_metro2',
      method: 'post_bankruptcy_tradeline_metro2_dispute_workflow',
    },
  },
  
  // Source-Denial / LexisNexis Case
  {
    id: 'bk_source_denial',
    name: 'Source Verification Challenge (Court Letter + LexisNexis)',
    category: 'bankruptcy',
    subcategory: 'source_denial',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: false,
      years_since_filing: 6,
      court_letter_present: true,
      court_no_direct_reporting: true,
      lexisnexis_trace_present: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_escalation_mov_attorney',
      method: 'bankruptcy_public_record_source_denial_workflow',
    },
  },
  
  // Attorney / Motion-to-Seal Case
  {
    id: 'bk_attorney_seal',
    name: 'Attorney / Motion-to-Seal Path',
    category: 'bankruptcy',
    subcategory: 'attorney',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: false,
      years_since_filing: 5,
      sealed_order_present: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_escalation_mov_attorney',
      method: 'bankruptcy_motion_to_seal_workflow',
    },
  },
  
  // Contradiction Risk Case
  {
    id: 'bk_contradiction_risk',
    name: 'Contradiction Risk - Prior Position Conflict',
    category: 'bankruptcy',
    subcategory: 'risk',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: true,
      years_since_filing: 4,
      contradiction_risk: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_escalation_mov_attorney',
      human_review_required: true,
    },
  },
  
  // MOV Escalation Case
  {
    id: 'bk_mov_escalation',
    name: 'Generic Verified Response - MOV Escalation',
    category: 'bankruptcy',
    subcategory: 'escalation',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_13',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: true,
      years_since_filing: 5,
      generic_verified_response: true,
      mov_not_provided: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_escalation_mov_attorney',
      method: 'bankruptcy_escalation_mov_workflow',
    },
  },
  
  // Wrong Association Case
  {
    id: 'bk_wrong_association',
    name: 'Wrong Association - Account Not in Schedule',
    category: 'bankruptcy',
    subcategory: 'wrong_association',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: true,
      years_since_filing: 3,
      tradeline_wrong_association: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_wrong_association',
      method: 'bankruptcy_wrong_association_dispute_workflow',
    },
  },
  
  // File Reset Case
  {
    id: 'bk_file_reset',
    name: 'File Reset - Prior Dispute Comments',
    category: 'bankruptcy',
    subcategory: 'file_reset',
    input: {
      has_bankruptcy: true,
      bankruptcy_type: 'chapter_7',
      bankruptcy_public_record: true,
      bankruptcy_attached_tradelines: false,
      years_since_filing: 4,
      prior_dispute_comments: true,
      fraud_alert_present: true,
    },
    expected: {
      dispute_lane: 'bankruptcy_public_record_dispute',
      method: 'comment_removal_file_reset_workflow',
    },
  },
];

// ============================================
// CATEGORY CONFIG
// ============================================

const CATEGORY_CONFIG = {
  standard: {
    label: 'Standard Credit',
    icon: FileText,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
  },
  bankruptcy: {
    label: 'Bankruptcy',
    icon: Scale,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
  },
};

const BANKRUPTCY_SUBCATEGORY_CONFIG = {
  public_record: { label: 'Public Record', icon: FileWarning, color: 'text-orange-400' },
  obsolete: { label: 'Obsolete', icon: AlertTriangle, color: 'text-red-400' },
  attached_tradeline_factual: { label: 'Tradeline Factual', icon: FileText, color: 'text-teal-400' },
  attached_tradeline_metro2: { label: 'Tradeline Metro 2', icon: Shield, color: 'text-amber-400' },
  source_denial: { label: 'Source Denial', icon: Users, color: 'text-teal-400' },
  attorney: { label: 'Attorney/Seal', icon: Gavel, color: 'text-pink-400' },
  risk: { label: 'Risk', icon: AlertTriangle, color: 'text-red-400' },
  escalation: { label: 'Escalation', icon: AlertCircle, color: 'text-yellow-400' },
  wrong_association: { label: 'Wrong Assoc.', icon: FileWarning, color: 'text-orange-400' },
  file_reset: { label: 'File Reset', icon: RefreshCw, color: 'text-emerald-400' },
};

const CLASSIFICATION_COLORS = {
  structural: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  recovery: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  isolated: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  structural_recovery: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  collection_notice: 'bg-red-500/20 text-red-400 border-red-500/30',
  bankruptcy_public_record_dispute: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  post_bankruptcy_tradeline_factual: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  post_bankruptcy_tradeline_metro2: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  bankruptcy_obsolete_reporting: 'bg-red-500/20 text-red-400 border-red-500/30',
  bankruptcy_wrong_association: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  bankruptcy_escalation_mov_attorney: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

// ============================================
// TEST RESULT STEP COMPONENT
// ============================================

const TestResultStep = ({ icon: Icon, title, content, color, details, badge }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`border ${color} rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg ${color.replace('border', 'bg').replace('/30', '/20')} flex items-center justify-center flex-shrink-0`}>
          <Icon className={color.replace('border-', 'text-').replace('/30', '')} size={16} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-xs text-slate-500">{title}</p>
            {badge && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${badge.color}`}>
                {badge.text}
              </span>
            )}
          </div>
          <p className="text-sm text-white font-medium">{content}</p>
          {details && (
            <>
              <button 
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-slate-400 hover:text-white mt-2 flex items-center gap-1"
              >
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {expanded ? 'Hide details' : 'Show details'}
              </button>
              {expanded && (
                <pre className="mt-2 text-xs text-slate-400 bg-stone-800 p-2 rounded overflow-x-auto max-h-[200px]">
                  {JSON.stringify(details, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const RuleTestingPage = () => {
  const { getAuthHeaders } = useAuth();
  const [inputData, setInputData] = useState(JSON.stringify(SAMPLE_TEST_CASES[0].input, null, 2));
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const runTest = async () => {
    try {
      const parsed = JSON.parse(inputData);
      setLoading(true);
      
      const response = await axios.post(`${API}/rules-methods/test-rules`, 
        { input: parsed },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      const result = response.data;
      setTestResult(result);
      
      if (result.using_persisted_data) {
        toast.success('Test completed using persisted rules');
      } else {
        toast.success('Test completed (using fallback rules)');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON input');
      } else {
        toast.error('Test failed: ' + (error.response?.data?.detail || error.message));
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestCase = (testId) => {
    const testCase = SAMPLE_TEST_CASES.find(t => t.id === testId);
    if (testCase) {
      setInputData(JSON.stringify(testCase.input, null, 2));
      setSelectedTestCase(testId);
      setTestResult(null);
    }
  };

  const filteredTestCases = categoryFilter === 'all' 
    ? SAMPLE_TEST_CASES 
    : SAMPLE_TEST_CASES.filter(tc => tc.category === categoryFilter);

  const standardCases = SAMPLE_TEST_CASES.filter(tc => tc.category === 'standard');
  const bankruptcyCases = SAMPLE_TEST_CASES.filter(tc => tc.category === 'bankruptcy');

  return (
    <div className="space-y-6" data-testid="rule-testing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FlaskConical className="text-teal-400" />
            Rule Testing
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Test the complete chain: Extract → Classify → Method → Prompt
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Filter:</span>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[140px] bg-stone-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cases</SelectItem>
              <SelectItem value="standard">Standard Credit</SelectItem>
              <SelectItem value="bankruptcy">Bankruptcy</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FlaskConical className="text-teal-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-teal-300">Test Your Rules</h3>
            <p className="text-sm text-teal-200/70 mt-1">
              Enter raw account data to see how the system will extract signals, classify the account, 
              select a method, and choose the appropriate prompt/template. 
              <strong className="text-teal-200"> Includes full bankruptcy dispute framework testing.</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Panel */}
        <div className="space-y-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Test Input</h3>
              <Select value={selectedTestCase} onValueChange={loadTestCase}>
                <SelectTrigger className="w-[250px] bg-stone-800 border-slate-700 text-white">
                  <SelectValue placeholder="Load test case..." />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 border-b border-slate-700">
                    Standard Credit Cases
                  </div>
                  {standardCases.map(tc => (
                    <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-amber-400 border-b border-slate-700 mt-2">
                    Bankruptcy Cases
                  </div>
                  {bankruptcyCases.map(tc => (
                    <SelectItem key={tc.id} value={tc.id}>
                      {tc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Textarea
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              placeholder="Enter JSON data..."
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm min-h-[300px]"
            />
            
            <div className="flex items-center gap-3 mt-4">
              <Button 
                onClick={runTest} 
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700 flex-1"
              >
                {loading ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Play size={16} className="mr-2" />
                )}
                Run Test
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setInputData('{}');
                  setTestResult(null);
                  setSelectedTestCase('');
                }}
                className="border-slate-700"
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Test Cases */}
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3">
              {categoryFilter === 'all' ? 'All Test Cases' : categoryFilter === 'bankruptcy' ? 'Bankruptcy Test Cases' : 'Standard Test Cases'}
              <span className="ml-2 text-slate-500">({filteredTestCases.length})</span>
            </h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {filteredTestCases.map(tc => {
                const catConfig = CATEGORY_CONFIG[tc.category];
                const subConfig = tc.subcategory ? BANKRUPTCY_SUBCATEGORY_CONFIG[tc.subcategory] : null;
                const CatIcon = catConfig?.icon || FileText;
                const SubIcon = subConfig?.icon || null;
                
                return (
                  <button
                    key={tc.id}
                    onClick={() => loadTestCase(tc.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedTestCase === tc.id 
                        ? 'border-teal-500 bg-teal-500/10' 
                        : `${catConfig.borderColor} hover:border-slate-600 bg-stone-800`
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CatIcon size={14} className={catConfig.color} />
                      <p className="text-sm text-white font-medium flex-1">{tc.name}</p>
                      {subConfig && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${catConfig.bgColor} ${subConfig.color}`}>
                          {subConfig.label}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Expected: {tc.expected.dispute_lane || tc.expected.classification || tc.expected.method}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Test Results</h3>
          
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ) : testResult ? (
            <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2">
              {/* Success Badge */}
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <CheckCircle className="text-emerald-400" size={20} />
                <span className="text-emerald-300 font-medium">Test Passed</span>
                <span className="text-xs text-emerald-400 ml-auto">{new Date(testResult.timestamp).toLocaleTimeString()}</span>
              </div>

              {/* Bankruptcy Context */}
              {testResult.bankruptcy_context && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="text-amber-400" size={16} />
                    <span className="text-amber-300 font-medium text-sm">Bankruptcy Context</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Type:</span>
                      <span className="text-white ml-1 font-medium">
                        {testResult.bankruptcy_context.bankruptcy_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400">Window:</span>
                      <span className="text-white ml-1 font-medium">
                        {testResult.bankruptcy_context.reporting_window_years} years
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400">Since Filing:</span>
                      <span className="text-white ml-1 font-medium">
                        {testResult.bankruptcy_context.years_since_filing} years
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Human Review Required Warning */}
              {testResult.routing?.human_review_required && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="text-red-400" size={16} />
                    <span className="text-red-300 font-medium text-sm">Human Review Required</span>
                  </div>
                  <p className="text-xs text-red-200/70 mt-1">
                    This case requires manual review before proceeding with dispute generation.
                  </p>
                </div>
              )}

              {/* Step 1: Extraction */}
              <TestResultStep
                icon={Filter}
                title="Step 1: Signal Extraction"
                content={`Extracted ${Object.keys(testResult.extraction).length} signals`}
                color="border-teal-500/30"
                details={testResult.extraction}
                badge={testResult.extraction_rules_matched?.length > 0 ? {
                  text: `${testResult.extraction_rules_matched.length} rules matched`,
                  color: 'bg-teal-500/20 text-teal-400'
                } : null}
              />

              <div className="flex justify-center">
                <ArrowRight size={20} className="text-slate-600" />
              </div>

              {/* Step 2: Classification/Routing */}
              <TestResultStep
                icon={GitBranch}
                title="Step 2: Classification / Routing"
                content={testResult.routing.matched_rule}
                color="border-orange-500/30"
                details={testResult.routing}
                badge={testResult.routing?.dispute_lane ? {
                  text: testResult.routing.dispute_lane.replace(/_/g, ' '),
                  color: CLASSIFICATION_COLORS[testResult.routing.dispute_lane] || 'bg-slate-500/20 text-slate-400'
                } : null}
              />

              {/* AI Recommendation */}
              {testResult.routing?.ai_recommendation_text && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-xs text-emerald-400 font-medium mb-1">AI Recommendation</p>
                  <p className="text-sm text-emerald-200">{testResult.routing.ai_recommendation_text}</p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2">
                <ArrowRight size={20} className="text-slate-600" />
                {testResult.routing?.recommended_source_first && (
                  <span className={`px-3 py-1 text-sm rounded-full border font-medium bg-slate-700 text-white`}>
                    {testResult.routing.source_display_name || testResult.routing.recommended_source_first}
                  </span>
                )}
              </div>

              {/* Step 3: Method Selection */}
              <TestResultStep
                icon={Library}
                title="Step 3: Method Selection"
                content={testResult.method?.method_name || testResult.method?.name || 'N/A'}
                color="border-emerald-500/30"
                details={testResult.method}
                badge={testResult.method?.method_family ? {
                  text: testResult.method.method_family,
                  color: 'bg-emerald-500/20 text-emerald-400'
                } : null}
              />

              <div className="flex justify-center">
                <ArrowRight size={20} className="text-slate-600" />
              </div>

              {/* Step 4: Prompt/Template */}
              <TestResultStep
                icon={FileText}
                title="Step 4: Output Selection"
                content={testResult.prompt.name}
                color="border-amber-500/30"
                details={{ prompt: testResult.prompt, template: testResult.template }}
              />

              {/* AI Hints */}
              {testResult.ai_hints && testResult.ai_hints.length > 0 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-400 font-medium mb-2">AI Hints ({testResult.ai_hints.length})</p>
                  <ul className="space-y-1">
                    {testResult.ai_hints.map((hint, i) => (
                      <li key={i} className="text-xs text-amber-200/80 flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        {hint}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Copy Result */}
              <Button
                variant="outline"
                className="w-full border-slate-700 mt-4"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(testResult, null, 2));
                  toast.success('Result copied to clipboard');
                }}
              >
                <Copy size={14} className="mr-2" />
                Copy Full Result
              </Button>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white">No Test Results</h3>
              <p className="text-slate-400 mt-1">Enter data and click "Run Test" to see results</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleTestingPage;
